/**
 * Register-and-claim wizard for /register page.
 *
 * States (data-step):
 *   already-linked          (when the account already owns a player; nothing to bind)
 *   account → check-email   (when not logged in)
 *   have-played → claim-search → claim-confirm → done
 *                            ↘ create-new → done
 *   have-played → create-new → done   (when user answers "No")
 *                           ↘ duplicate-check → claim-confirm → done   (server found same-name players)
 *                                            ↘ create-new (confirm_new) → done
 */

import { __, _n, sprintf } from '../lib/i18n.js';

const cfg = () => window.bigSquashPlayerRegister || {};
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 200;

function api(path, opts = {}) {
    const { restUrl, restNonce } = cfg();
    return fetch(`${restUrl}${path}`, {
        credentials: 'same-origin',
        ...opts,
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': restNonce,
            ...(opts.headers || {}),
        },
    });
}

async function readJson(res) {
    try {
        return await res.json();
    } catch {
        return null;
    }
}

function escapeHtml(text) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(text || '').replace(/[&<>"']/g, (m) => map[m]);
}

// Strip diacritics so the 3-char threshold counts "á" the same as "a".
// U+0300..U+036F is the Combining Diacritical Marks block.
const COMBINING_MARKS_RE = new RegExp('[\\u0300-\\u036f]', 'g');
function fold(s) {
    return String(s || '')
        .normalize('NFD')
        .replace(COMBINING_MARKS_RE, '')
        .toLowerCase()
        .trim();
}

function showStep(root, name) {
    root.querySelectorAll('.big-squash-step').forEach((el) => {
        el.hidden = el.dataset.step !== name;
    });
}

function showError(form, msg) {
    const el = form.querySelector('[data-error]');
    if (el) {
        el.textContent = msg;
        el.hidden = false;
    }
}

function clearError(form) {
    const el = form.querySelector('[data-error]');
    if (el) {
        el.textContent = '';
        el.hidden = true;
    }
}

function init() {
    const root = document.querySelector('[data-big-squash-register]');
    if (!root) return;

    const isLoggedIn = !!cfg().isLoggedIn;

    if (!isLoggedIn) {
        bindAccountStep(root);
    } else {
        bindHavePlayed(root);
        bindClaimSearch(root);
        bindClaimConfirm(root);
        bindCreateNew(root);
        bindBackButtons(root);
    }
}

function bindAccountStep(root) {
    const form = root.querySelector('[data-big-squash-register-form]');
    if (!form) return;
    let registeredEmail = '';

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError(form);
        const data = Object.fromEntries(new FormData(form).entries());
        const res = await api('/auth/register', { method: 'POST', body: JSON.stringify(data) });
        const body = await readJson(res);
        if (!res.ok) {
            showError(form, (body && body.message) || __('Registration failed'));
            if (body && body.code === 'email_taken') appendResendLink(form);
            return;
        }
        registeredEmail = data.email || '';
        showStep(root, 'check-email');
    });

    // "Send the link again" on the check-email step
    const resendBtn = root.querySelector('[data-big-squash-resend]');
    const resendStatus = root.querySelector('[data-resend-status]');
    if (resendBtn) {
        resendBtn.addEventListener('click', async () => {
            if (!registeredEmail) return;
            resendBtn.disabled = true;
            if (resendStatus) {
                resendStatus.textContent = __('Sending…');
                resendStatus.hidden = false;
            }
            const res = await api('/auth/resend-verification', {
                method: 'POST',
                body: JSON.stringify({ email: registeredEmail }),
            });
            const body = await readJson(res);
            if (resendStatus) {
                resendStatus.textContent = (body && body.message)
                    || (res.ok ? __('A new link is on its way.') : __('Could not send the email. Please try again later.'));
            }
            resendBtn.disabled = false;
        });
    }
}

/**
 * The address is already registered: point the user at the login page, which
 * opens its "resend verification" box when ?resend=1 is present.
 */
function appendResendLink(form) {
    const errEl = form.querySelector('[data-error]');
    const loginUrl = cfg().loginUrl;
    if (!errEl || !loginUrl) return;
    const link = document.createElement('a');
    link.href = `${loginUrl}${loginUrl.includes('?') ? '&' : '?'}resend=1`;
    link.textContent = __('Log in or resend the verification email');
    errEl.appendChild(document.createTextNode(' '));
    errEl.appendChild(link);
}

function bindHavePlayed(root) {
    root.querySelectorAll('[data-big-squash-played]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const answer = btn.dataset.bigSquashPlayed;
            if (answer === 'no') {
                showStep(root, 'create-new');
            } else {
                showStep(root, 'claim-search');
                const input = root.querySelector('[data-big-squash-claim-search-input]');
                if (input) input.focus();
            }
        });
    });
}

function bindClaimSearch(root) {
    const input = root.querySelector('[data-big-squash-claim-search-input]');
    const list = root.querySelector('[data-candidate-list]');
    const status = root.querySelector('[data-search-status]');
    if (!input || !list || !status) return;

    let debounceTimer = null;
    let activeRequestId = 0;

    function setStatus(msg) {
        status.textContent = msg || '';
    }

    function renderResults(results, rawValue) {
        if (!results.length) {
            list.innerHTML = '';
            setStatus(sprintf(__('No matches for "%s". Try another spelling, or create a new profile.'), rawValue));
            return;
        }
        setStatus(sprintf(_n('%d match', '%d matches', results.length), results.length));
        renderCandidateList(root, list, results, 'claim-search');
    }

    async function runSearch(rawValue) {
        const folded = fold(rawValue);
        if (folded.length < SEARCH_MIN_CHARS) {
            list.innerHTML = '';
            setStatus(folded.length === 0 ? '' : sprintf(__('Type at least %d letters…'), SEARCH_MIN_CHARS));
            return;
        }
        const requestId = ++activeRequestId;
        setStatus(__('Searching…'));
        const res = await api(`/players/search-unclaimed?q=${encodeURIComponent(rawValue.trim())}`);
        // Drop stale responses if the user has typed more since
        if (requestId !== activeRequestId) return;
        const body = await readJson(res);
        if (!res.ok || !Array.isArray(body)) {
            setStatus(__('Search failed. Please try again.'));
            return;
        }
        renderResults(body, rawValue.trim());
    }

    input.addEventListener('input', (e) => {
        const value = e.target.value;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => runSearch(value), SEARCH_DEBOUNCE_MS);
    });

    const skipBtn = root.querySelector('[data-big-squash-skip-to-create]');
    if (skipBtn) {
        skipBtn.addEventListener('click', () => showStep(root, 'create-new'));
    }
}

/**
 * Fill a <ul> with one "claim this player" button per candidate. backTo is the
 * step the confirm screen's Back button should return to.
 */
function renderCandidateList(root, listEl, candidates, backTo) {
    listEl.innerHTML = '';
    candidates.forEach((c) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'big-squash-btn';
        const flag = c.country
            ? `<span class="fi fi-${c.country.toLowerCase()}" aria-hidden="true"></span> `
            : '';
        btn.innerHTML = `${flag}${escapeHtml(c.full_name)}`;
        btn.addEventListener('click', () => selectCandidate(root, c, backTo));
        li.appendChild(btn);
        listEl.appendChild(li);
    });
}

function selectCandidate(root, candidate, backTo = 'claim-search') {
    const confirmForm = root.querySelector('[data-big-squash-claim-confirm]');
    if (!confirmForm) return;
    confirmForm.dataset.backTo = backTo;
    confirmForm.querySelector('[name="player_id"]').value = candidate.id;
    const nameEl = root.querySelector('[data-claim-candidate-name]');
    if (nameEl) nameEl.textContent = candidate.full_name;
    const linkEl = root.querySelector('[data-claim-candidate-link]');
    if (linkEl) {
        const base = cfg().playerProfileBase || '';
        linkEl.href = candidate.slug ? `${base}${candidate.slug}` : '#';
    }
    showStep(root, 'claim-confirm');
}

function bindClaimConfirm(root) {
    const form = root.querySelector('[data-big-squash-claim-confirm]');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError(form);
        const data = Object.fromEntries(new FormData(form).entries());
        data.player_id = parseInt(data.player_id, 10);
        if (data.phone) data.phone = data.phone.replace(/\s+/g, '');
        const res = await api('/players/claim', { method: 'POST', body: JSON.stringify(data) });
        const body = await readJson(res);
        if (!res.ok) {
            showError(form, (body && body.message) || __('Could not claim profile'));
            return;
        }
        showStep(root, 'done');
    });
}

function bindCreateNew(root) {
    const form = root.querySelector('[data-big-squash-create-form]');
    if (!form) return;
    const duplicateList = root.querySelector('[data-duplicate-list]');

    async function submit(confirmNew) {
        clearError(form);
        const data = Object.fromEntries(new FormData(form).entries());
        if (data.phone) data.phone = data.phone.replace(/\s+/g, '');
        if (confirmNew) data.confirm_new = true;
        const res = await api('/players/me', { method: 'POST', body: JSON.stringify(data) });
        const body = await readJson(res);
        if (res.ok) {
            showStep(root, 'done');
            return;
        }
        // Same-name players already exist: offer them before creating a duplicate
        const candidates = body && body.code === 'possible_duplicate' && body.data
            ? body.data.candidates
            : null;
        if (Array.isArray(candidates) && candidates.length && duplicateList) {
            renderCandidateList(root, duplicateList, candidates, 'duplicate-check');
            showStep(root, 'duplicate-check');
            return;
        }
        showError(form, (body && body.message) || __('Could not create profile'));
    }

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        submit(false);
    });
    const createAnyway = root.querySelector('[data-big-squash-create-anyway]');
    if (createAnyway) {
        createAnyway.addEventListener('click', () => submit(true));
    }
}

function bindBackButtons(root) {
    root.querySelectorAll('[data-big-squash-claim-back]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const form = btn.closest('form');
            showStep(root, (form && form.dataset.backTo) || 'claim-search');
        });
    });
    root.querySelectorAll('[data-big-squash-back-to-create]').forEach((btn) => {
        btn.addEventListener('click', () => showStep(root, 'create-new'));
    });
    root.querySelectorAll('[data-big-squash-back-to-have-played]').forEach((btn) => {
        btn.addEventListener('click', () => showStep(root, 'have-played'));
    });
    root.querySelectorAll('[data-big-squash-back-from-create]').forEach((btn) => {
        btn.addEventListener('click', () => showStep(root, 'have-played'));
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
